import React from 'react';
import classNames from 'classnames';

import Store from 'store';
import Locale from 'data/locale';

import Data from 'data';
import Select from 'ui/select';
import { useDragDrop, useItemMover } from 'components/dragDrop';
import { itemAllowed, itemSlotValid } from 'logic/item';
import { chainedRef } from 'utils';
import { D3TooltipItem } from 'tooltips';
import { profileCanEquipSet, profileEquipSet } from 'logic/profileReducers';
import ItemEditor, { ItemSelectAncient, ItemSelectItem } from './ItemEditor';
import EditingContext from 'store/editingContext';

const _L = Locale("ui-equipment.js");

function EquipmentSlot({ profile, slot, editGem, follower }) {
  const itemMover = useItemMover();
  
  let data = follower ? profile.followerItems[slot] : profile.items[slot];
  if (editGem && data?.gems?.[0]) {
    data = data?.gems?.[0];
  }

  const item = data && Data.itemById[data.id];
  const itemType = item && Data.itemTypes[item.type];

  const [dragClass, drag, drop] = useDragDrop("ITEM", {
    canDrag: () => item != null && !editGem,
    begin: () => ({type: "ITEM", id: item.id}),
    end: (item, monitor) => itemMover({location: "equipment", slot}, monitor.getDropResult()),
  }, {
    canDrop: item => item.type === "ITEM" && !editGem && itemAllowed(profile, slot, item.id, follower),
    drop: () => ({location: "equipment", slot}),
  });
  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop]);

  let quality = item?.quality;
  if (item?.ancient === 'sanctified') quality = 'sanctified';

  return (
    <D3TooltipItem data={data} disabled={!item || dragClass === "d3p-dragging"}>
      <div className={classNames("d3p-EquipmentSlot d3p-DragDrop", dragClass, item && `d3p-quality-${quality}`, {"d3p-empty": !item})} ref={nodeRef}>
        {!!item && (
          <div className="d3p-ItemGradient">
            <div className="d3p-ItemGlow"/>
          </div>
        )}
        <div className={classNames("d3p-EquipmentIcon", item && `d3p-slot-${(itemType && itemType.slot) || "custom"}`)}
            style={item && {backgroundImage: `url(${Data.getItemIcon(data.id, profile)})`}}/>
      </div>
    </D3TooltipItem>
  );
}

const SelectSlot = React.memo(function SelectSlot({ charClass, slot, setSlot, disabled }) {
  const options = React.useMemo(() => Object.keys(Data.itemSlots).filter(slot => itemSlotValid(charClass, slot)), [charClass]);
  const getOptions = React.useCallback(() => options.map(slot => ({id: slot, name: Data.itemSlots[slot].name})), [options]);
  const onChange = React.useCallback(value => setSlot(value.id), [setSlot]);

  return (
    <Select options={getOptions}
            value={slot ? ({id: slot, name: Data.itemSlots[slot].name}) : null}
            onChange={onChange}
            maxHeight={400}
            placeholder={_L`Select Slot`}
            disabled={!charClass || disabled}
    />
  );
});

export default React.memo(function EquipmentItem() {
  const profile = Store.useProfile();
  const [, setEditing] = EditingContext.useState("itemSlotEditing");
  const follower = (EditingContext.useStateView("paperdollMode") === "follower");
  let [slot, setSlotOrig] = EditingContext.useState("itemSlot");
  let [editGem, setEditGem] = React.useState(false);

  const charClass = follower ? profile.follower : profile.class;
  if (!charClass || !itemSlotValid(charClass, slot)) {
    slot = null;
  }
  if (editGem) {
    const item = slot && (follower ? profile.followerItems[slot] : profile.items[slot]);
    if (!item || !item.gems || !item.gems?.[0]?.id) {
      editGem = false;
    }
  }

  React.useEffect(() => {
    if (!editGem) setEditing(slot);
    return () => setEditing(null);
  }, [setEditing, slot, editGem]);

  const slotRef = React.useRef();

  const setSlot = React.useCallback((slot, gem=false) => {
    setSlotOrig(slot);
    setEditGem(gem);
  }, [setSlotOrig, setEditGem]);

  const selectSlot = React.useCallback(({ type, slot: targetSlot }) => {
    if (type !== "item") return;
    if (slot === targetSlot && !editGem) {
      if (!(follower ? profile.followerItems : profile.items)[slot]) {
        slotRef.current.focus();
      }
      return true;
    } else if (itemSlotValid(follower ? profile.follower : profile.class, targetSlot)) {
      setSlot(targetSlot, false);
    }
  }, [profile, slot, setSlot, follower, editGem]);
  EditingContext.useHandler(selectSlot);

  const onEditGem = React.useCallback(() => setEditGem(true), [setEditGem]);

  let item = slot && (follower ? profile.followerItems[slot] : profile.items[slot]);
  if (editGem && item?.gems?.[0]) {
    item = item?.gems?.[0];
  }
  const dispatch = Store.useDispatch();
  const onChange = React.useCallback(update => {
    function onUpdate(item, ...args) {
      if (editGem) {
        if (item?.gems?.[0]) {
          item = {...item};
          item.gems = item.gems.slice();
          item.gems[0] = typeof update === 'function' ? update(item.gems[0], ...args) : update;
        }
        return item;
      } else {
        return typeof update === 'function' ? update(item, ...args) : update;
      }
    };
    if (follower) {
      dispatch.profile.setFollowerItem(slot, onUpdate);
    } else {
      dispatch.profile.setItem(slot, onUpdate);
    }
  }, [slot, follower, dispatch, editGem]);

  const onEquipSet = React.useCallback(() => {
    dispatch.setProfile(profile => {
      const item = follower ? profile.followerItems[slot] : profile.items[slot];
      const info = item && Data.itemById[item.id];
      if (!info || !info.set) return profile;
      return profileEquipSet(profile, info.set, follower);
    });
  }, [dispatch, slot, follower]);

  const equipSet = React.useMemo(() => {
    const item = slot && (follower ? profile.followerItems[slot] : profile.items[slot]);
    const info = item && Data.itemById[item.id];
    if (!info || !info.set) return null;
    if (!profileCanEquipSet(profile, info.set, follower)) return null;
    return <div className="d3p-ItemEquipSet" onClick={onEquipSet}>{_L`Equip full set`}</div>;
  }, [profile, slot, onEquipSet, follower]);

  return (
    <div className="d3p-Section d3p-expanded d3p-always">
      <h3>{_L`Gear Selection`}</h3>
      <p className="d3p-tip">{_L`Choose a gear slot from below, then select the item and rarity you want to use.`}</p>

      <div className="d3p-EquipmentSlotSelector">
        <EquipmentSlot profile={profile} slot={slot} editGem={editGem} follower={follower}/>
        <div className="d3p-EquipmentSlotHeader">
          <SelectSlot charClass={charClass} slot={slot} setSlot={setSlot} disabled={editGem}/>
          <ItemSelectItem profile={profile} follower={follower} slot={slot} item={item} onChange={onChange} ref={slotRef} disabled={!slot || editGem}/>
          <ItemSelectAncient item={item} onChange={onChange}/>
        </div>
      </div>

      {equipSet}
      {!!item && (
        <ItemEditor follower={follower} slot={slot} item={item} onChange={onChange} className="d3p-main-editor" editGem={onEditGem}/>
      )}
    </div>
  );
});
